import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from './AuthProvider';
import DatePicker from './DatePicker';
import '../styles/AdminDashboard.css';

interface DashboardStats {
  users: {
    total_users: number;
    regular_users: number;
    admin_users: number;
    verified_users: number;
  };
  bookings: {
    total_bookings: number;
    pending_bookings: number;
    confirmed_bookings: number;
    completed_bookings: number;
    cancelled_bookings: number;
  };
  contacts: {
    total_messages: number;
    unread_messages: number;
    read_messages: number;
    replied_messages: number;
  };
  recent: {
    new_users: number;
    new_bookings: number;
  };
}

interface User {
  id: number;
  name: string;
  email: string;
  phone: string;
  address: string;
  role: string;
  is_verified: boolean;
  created_at: string;
}

interface Booking {
  id: number;
  user_name: string;
  user_email: string;
  user_phone: string;
  service_type: string;
  consultation_type: string;
  date: string;
  time: string;
  status: string;
  amount: number;
  created_at: string;
  lawyer_name?: string;
}

const AdminDashboard: React.FC = () => {
  const { user } = useContext(AuthContext) || { user: null };
  const [activeTab, setActiveTab] = useState<'dashboard' | 'users' | 'bookings'>('dashboard');
  const [userFilter, setUserFilter] = useState('all');
  const [userSearchTerm, setUserSearchTerm] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [bookingSearchTerm, setBookingSearchTerm] = useState('');
  const [bookingStatusFilter, setBookingStatusFilter] = useState('all');
  const [bookingDateFrom, setBookingDateFrom] = useState('');
  const [bookingDateTo, setBookingDateTo] = useState('');
  // Dummy data for UI testing
  const [stats, setStats] = useState<DashboardStats>({
    users: {
      total_users: 156,
      regular_users: 154,
      admin_users: 2,
      verified_users: 142
    },
    bookings: {
      total_bookings: 89,
      pending_bookings: 12,
      confirmed_bookings: 45,
      completed_bookings: 28,
      cancelled_bookings: 4
    },
    contacts: {
      total_messages: 34,
      unread_messages: 8,
      read_messages: 18,
      replied_messages: 8
    },
    recent: {
      new_users: 23,
      new_bookings: 15
    }
  });

  const [users, setUsers] = useState<User[]>([
    {
      id: 1,
      name: "Rahul Sharma",
      email: "rahul.sharma@gmail.com",
      phone: "9876543210",
      address: "123 CP, New Delhi",
      role: "user",
      is_verified: true,
      created_at: "2024-01-15T10:30:00Z"
    },
    {
      id: 2,
      name: "Priya Singh",
      email: "priya.singh@gmail.com",
      phone: "9876543211",
      address: "456 Gurgaon, Haryana",
      role: "user",
      is_verified: true,
      created_at: "2024-01-20T14:22:00Z"
    },
    {
      id: 3,
      name: "Amit Kumar",
      email: "amit.kumar@gmail.com",
      phone: "9876543212",
      address: "789 Noida, UP",
      role: "user",
      is_verified: false,
      created_at: "2024-02-01T09:15:00Z"
    },
    {
      id: 4,
      name: "Sneha Patel",
      email: "sneha.patel@gmail.com",
      phone: "9876543213",
      address: "321 Mumbai, Maharashtra",
      role: "user",
      is_verified: true,
      created_at: "2024-02-05T16:45:00Z"
    },
    {
      id: 5,
      name: "WeLegal Admin",
      email: "admin@welegal.co",
      phone: "9999999999",
      address: "Admin Office",
      role: "admin",
      is_verified: true,
      created_at: "2024-01-01T00:00:00Z"
    }
  ]);

  const [bookings, setBookings] = useState<Booking[]>([
    {
      id: 1,
      user_name: "Rahul Sharma",
      user_email: "rahul.sharma@gmail.com",
      user_phone: "9876543210",
      service_type: "civil",
      consultation_type: "virtual",
      date: "2025-09-03",
      time: "10:00",
      status: "confirmed",
      amount: 2000,
      created_at: "2024-02-10T10:30:00Z",
      lawyer_name: "Adv. Vikash Mehta"
    },
    {
      id: 2,
      user_name: "Priya Singh",
      user_email: "priya.singh@gmail.com",
      user_phone: "9876543211",
      service_type: "matrimonial",
      consultation_type: "doorstep",
      date: "2025-09-03",
      time: "14:00",
      status: "pending",
      amount: 3000,
      created_at: "2024-02-11T14:22:00Z"
    },
    {
      id: 3,
      user_name: "Amit Kumar",
      user_email: "amit.kumar@gmail.com",
      user_phone: "9876543212",
      service_type: "criminal",
      consultation_type: "virtual",
      date: "2024-02-14",
      time: "11:00",
      status: "completed",
      amount: 2500,
      created_at: "2024-02-08T09:15:00Z",
      lawyer_name: "Adv. Rajesh Gupta"
    },
    {
      id: 4,
      user_name: "Sneha Patel",
      user_email: "sneha.patel@gmail.com",
      user_phone: "9876543213",
      service_type: "arbitration",
      consultation_type: "doorstep",
      date: "2024-02-20",
      time: "15:00",
      status: "cancelled",
      amount: 4000,
      created_at: "2024-02-12T16:45:00Z"
    },
    {
      id: 5,
      user_name: "Kiran Reddy",
      user_email: "kiran.reddy@gmail.com",
      user_phone: "9876543214",
      service_type: "mediation",
      consultation_type: "virtual",
      date: "2024-02-18",
      time: "16:00",
      status: "pending",
      amount: 2200,
      created_at: "2024-02-13T11:20:00Z"
    }
  ]);
  const [isLoading, setIsLoading] = useState(false);

  // Disabled API calls for UI testing
  // useEffect(() => {
  //   if (user?.role === 'admin') {
  //     loadDashboardStats();
  //   }
  // }, [user]);

  const getAuthHeader = () => ({
    'Authorization': `Bearer ${localStorage.getItem('accessToken')}`,
    'Content-Type': 'application/json'
  });

  const loadDashboardStats = async () => {
    try {
      setIsLoading(true);
      const response = await fetch('http://localhost:5000/api/v1/admin/dashboard/stats', {
        headers: getAuthHeader()
      });
      const result = await response.json();
      
      if (result.success) {
        setStats(result.data);
      }
    } catch (error) {
      console.error('Error loading dashboard stats:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const loadUsers = async () => {
    try {
      setIsLoading(true);
      const response = await fetch('http://localhost:5000/api/v1/admin/users', {
        headers: getAuthHeader()
      });
      const result = await response.json();
      
      if (result.success) {
        setUsers(result.data.users);
      }
    } catch (error) {
      console.error('Error loading users:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const loadBookings = async () => {
    try {
      setIsLoading(true);
      const response = await fetch('http://localhost:5000/api/v1/admin/bookings', {
        headers: getAuthHeader()
      });
      const result = await response.json();
      
      if (result.success) {
        setBookings(result.data.bookings);
      }
    } catch (error) {
      console.error('Error loading bookings:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const updateBookingStatus = async (bookingId: number, status: string) => {
    try {
      const response = await fetch(`http://localhost:5000/api/v1/admin/bookings/${bookingId}/status`, {
        method: 'PATCH',
        headers: getAuthHeader(),
        body: JSON.stringify({ status })
      });
      
      if (response.ok) {
        alert('Booking status updated successfully');
        loadBookings(); // Refresh bookings
      }
    } catch (error) {
      console.error('Error updating booking status:', error);
      alert('Failed to update booking status');
    }
  };

  const handleTabChange = (tab: 'dashboard' | 'users' | 'bookings') => {
    setActiveTab(tab);
    // Disabled API calls for UI testing
    // if (tab === 'users') {
    //   loadUsers();
    // } else if (tab === 'bookings') {
    //   loadBookings();
    // }
  };

  // Temporarily disabled for testing
  // if (!user || user.role !== 'admin') {
  //   return (
  //     <div className="admin-access-denied">
  //       <h2>Access Denied</h2>
  //       <p>You need admin privileges to access this page.</p>
  //     </div>
  //   );
  // }

  return (
    <div className="admin-layout">
      {/* Sidebar */}
      <div className="admin-sidebar">
        <div className="sidebar-header">
          <h2>WeLegal Admin</h2>
        </div>
        
        <nav className="sidebar-nav">
          <button 
            className={`nav-item ${activeTab === 'dashboard' ? 'active' : ''}`}
            onClick={() => handleTabChange('dashboard')}
          >
            <i className="fas fa-tachometer-alt"></i>
            <span>Dashboard</span>
          </button>
          <button 
            className={`nav-item ${activeTab === 'users' ? 'active' : ''}`}
            onClick={() => handleTabChange('users')}
          >
            <i className="fas fa-users"></i>
            <span>Users</span>
          </button>
          <button 
            className={`nav-item ${activeTab === 'bookings' ? 'active' : ''}`}
            onClick={() => handleTabChange('bookings')}
          >
            <i className="fas fa-calendar-check"></i>
            <span>Bookings</span>
          </button>
        </nav>
        
        <div className="sidebar-footer">
          <div className="powered-by">
            <span>Powered by Proctur</span>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="admin-main">
        {/* Header */}
        <div className="admin-header">
          <div className="header-title">
            <h1>{activeTab === 'dashboard' ? 'Dashboard Overview' : 
                 activeTab === 'users' ? 'User Management' : 
                 'Booking Management'}</h1>
          </div>
          <div className="admin-user-info">
            <span>Welcome, {user ? `${user.firstName} ${user.lastName}` : 'Admin User'}</span>
            <span className="admin-badge">Admin</span>
            <button 
              className="logout-btn"
              onClick={() => {
                localStorage.removeItem('accessToken');
                localStorage.removeItem('refreshToken');
                window.location.href = '/';
              }}
            >
              <i className="fas fa-sign-out-alt"></i>
              Logout
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="admin-content">
        {isLoading && <div className="loading">Loading...</div>}

        {activeTab === 'dashboard' && stats && (
          <div className="dashboard-stats">
            {/* Welcome Section */}
            <div className="welcome-section">
              <div className="welcome-content">
                <h2>Welcome back, {user ? `${user.firstName}` : 'Admin'}! 👋</h2>
                <p>Here's what's happening with your legal platform today.</p>
              </div>
              <div className="ai-quote">
                <div className="quote-icon">
                  <i className="fas fa-robot"></i>
                </div>
                <blockquote>
                  "Justice is truth in action. Your platform connects people with legal expertise, 
                  making justice more accessible one consultation at a time."
                </blockquote>
                <cite>— AI Legal Assistant</cite>
              </div>
            </div>

            <div className="stats-grid">
              <div className="stat-card">
                <div className="stat-icon users">
                  <i className="fas fa-users"></i>
                </div>
                <div className="stat-info">
                  <h3>{stats.users.total_users}</h3>
                  <p>Total Users</p>
                  <small>{stats.recent.new_users} new this week</small>
                </div>
              </div>

              <div className="stat-card">
                <div className="stat-icon bookings">
                  <i className="fas fa-calendar"></i>
                </div>
                <div className="stat-info">
                  <h3>{stats.bookings.total_bookings}</h3>
                  <p>Total Bookings</p>
                  <small>{stats.recent.new_bookings} new this week</small>
                </div>
              </div>

              <div className="stat-card">
                <div className="stat-icon pending">
                  <i className="fas fa-clock"></i>
                </div>
                <div className="stat-info">
                  <h3>{stats.bookings.pending_bookings}</h3>
                  <p>Pending Bookings</p>
                </div>
              </div>

              <div className="stat-card">
                <div className="stat-icon messages">
                  <i className="fas fa-envelope"></i>
                </div>
                <div className="stat-info">
                  <h3>{stats.contacts.unread_messages}</h3>
                  <p>Unread Messages</p>
                </div>
              </div>
            </div>

            {/* Today's Consultations */}
            <div className="today-consultations">
              <h4>Today's Consultations</h4>
              <div className="consultation-list">
                {bookings
                  .filter(booking => {
                    const today = new Date().toISOString().split('T')[0];
                    return booking.date === today && (booking.status === 'confirmed' || booking.status === 'pending');
                  })
                  .map(booking => (
                    <div key={booking.id} className="consultation-item">
                      <div className="consultation-time">
                        <i className="fas fa-clock"></i>
                        <span>{booking.time}</span>
                      </div>
                      <div className="consultation-details">
                        <h5>{booking.user_name}</h5>
                        <p>{booking.service_type} • {booking.consultation_type}</p>
                        <span className={`consultation-status ${booking.status}`}>
                          {booking.status}
                        </span>
                      </div>
                      <div className="consultation-amount">
                        ₹{booking.amount}
                      </div>
                    </div>
                  ))}
                {bookings.filter(booking => {
                  const today = new Date().toISOString().split('T')[0];
                  return booking.date === today && (booking.status === 'confirmed' || booking.status === 'pending');
                }).length === 0 && (
                  <div className="no-consultations">
                    <i className="fas fa-calendar-check"></i>
                    <p>No consultations scheduled for today</p>
                  </div>
                )}
              </div>
            </div>

            <div className="charts-section">
              <div className="chart-card">
                <h4>User Overview</h4>
                <div className="chart-stats">
                  <div className="chart-item">
                    <span className="label">Regular Users:</span>
                    <span className="value">{stats.users.regular_users}</span>
                  </div>
                  <div className="chart-item">
                    <span className="label">Verified Users:</span>
                    <span className="value">{stats.users.verified_users}</span>
                  </div>
                </div>
              </div>

              <div className="chart-card">
                <h4>Booking Status</h4>
                <div className="chart-stats">
                  <div className="chart-item">
                    <span className="label">Confirmed:</span>
                    <span className="value">{stats.bookings.confirmed_bookings}</span>
                  </div>
                  <div className="chart-item">
                    <span className="label">Completed:</span>
                    <span className="value">{stats.bookings.completed_bookings}</span>
                  </div>
                  <div className="chart-item">
                    <span className="label">Cancelled:</span>
                    <span className="value">{stats.bookings.cancelled_bookings}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'users' && (
          <div className="users-section">
            <div className="section-header">
              <h3>All Users</h3>
              <div className="filter-controls">
                <div className="search-box">
                  <i className="fas fa-search"></i>
                  <input
                    type="text"
                    placeholder="Search users by name, email, or phone..."
                    value={userSearchTerm}
                    onChange={(e) => setUserSearchTerm(e.target.value)}
                    className="search-input"
                  />
                </div>
                <div className="date-range">
                  <DatePicker
                    value={dateFrom}
                    onChange={setDateFrom}
                    placeholder="From Date"
                  />
                  <span className="date-separator">to</span>
                  <DatePicker
                    value={dateTo}
                    onChange={setDateTo}
                    placeholder="To Date"
                  />
                </div>
                <select 
                  value={userFilter} 
                  onChange={(e) => setUserFilter(e.target.value)}
                  className="filter-select"
                >
                  <option value="all">All Users</option>
                  <option value="verified">Verified Only</option>
                  <option value="unverified">Unverified Only</option>
                  <option value="admin">Admin Users</option>
                  <option value="user">Regular Users</option>
                </select>
              </div>
            </div>
            <div className="data-table">
              <table>
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Phone</th>
                    <th>Role</th>
                    <th>Verified</th>
                    <th>Joined</th>
                  </tr>
                </thead>
                <tbody>
                  {users
                    .filter(user => {
                      // Search filter
                      const searchMatch = userSearchTerm === '' || 
                        user.name.toLowerCase().includes(userSearchTerm.toLowerCase()) ||
                        user.email.toLowerCase().includes(userSearchTerm.toLowerCase()) ||
                        user.phone.includes(userSearchTerm);
                      
                      // Status filter
                      let statusMatch = true;
                      if (userFilter === 'verified') statusMatch = user.is_verified;
                      else if (userFilter === 'unverified') statusMatch = !user.is_verified;
                      else if (userFilter === 'admin') statusMatch = user.role === 'admin';
                      else if (userFilter === 'user') statusMatch = user.role === 'user';
                      
                      // Date range filter
                      let dateMatch = true;
                      const userJoinDate = new Date(user.created_at).toISOString().split('T')[0];
                      if (dateFrom && userJoinDate < dateFrom) dateMatch = false;
                      if (dateTo && userJoinDate > dateTo) dateMatch = false;
                      
                      return searchMatch && statusMatch && dateMatch;
                    })
                    .map((user, index) => (
                    <tr key={user.id}>
                      <td>{index + 1}</td>
                      <td>{user.name}</td>
                      <td>{user.email}</td>
                      <td>{user.phone}</td>
                      <td>
                        <span className={`role-badge ${user.role}`}>
                          {user.role}
                        </span>
                      </td>
                      <td>
                        <span className={`status-badge ${user.is_verified ? 'verified' : 'unverified'}`}>
                          {user.is_verified ? 'Verified' : 'Unverified'}
                        </span>
                      </td>
                      <td>{new Date(user.created_at).toLocaleDateString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {activeTab === 'bookings' && (
          <div className="bookings-section">
            <div className="section-header">
              <h3>All Bookings</h3>
              <div className="filter-controls">
                <div className="search-box">
                  <i className="fas fa-search"></i>
                  <input
                    type="text"
                    placeholder="Search bookings by name, email, or service..."
                    value={bookingSearchTerm}
                    onChange={(e) => setBookingSearchTerm(e.target.value)}
                    className="search-input"
                  />
                </div>
                <div className="date-range">
                  <DatePicker
                    value={bookingDateFrom}
                    onChange={setBookingDateFrom}
                    placeholder="From Date"
                  />
                  <span className="date-separator">to</span>
                  <DatePicker
                    value={bookingDateTo}
                    onChange={setBookingDateTo}
                    placeholder="To Date"
                  />
                </div>
                <select 
                  value={bookingStatusFilter} 
                  onChange={(e) => setBookingStatusFilter(e.target.value)}
                  className="filter-select"
                >
                  <option value="all">All Status</option>
                  <option value="pending">Pending</option>
                  <option value="confirmed">Confirmed</option>
                  <option value="completed">Completed</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>
            </div>
            <div className="data-table">
              <table>
                <thead>
                  <tr>
                    <th>#</th>
                    <th>User</th>
                    <th>Service</th>
                    <th>Type</th>
                    <th>Date & Time</th>
                    <th>Amount</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {bookings
                    .filter(booking => {
                      // Search filter
                      const searchMatch = bookingSearchTerm === '' || 
                        booking.user_name.toLowerCase().includes(bookingSearchTerm.toLowerCase()) ||
                        booking.user_email.toLowerCase().includes(bookingSearchTerm.toLowerCase()) ||
                        booking.service_type.toLowerCase().includes(bookingSearchTerm.toLowerCase());
                      
                      // Status filter
                      const statusMatch = bookingStatusFilter === 'all' || booking.status === bookingStatusFilter;
                      
                      // Date range filter
                      let dateMatch = true;
                      if (bookingDateFrom && booking.date < bookingDateFrom) dateMatch = false;
                      if (bookingDateTo && booking.date > bookingDateTo) dateMatch = false;
                      
                      return searchMatch && statusMatch && dateMatch;
                    })
                    .map((booking, index) => (
                    <tr key={booking.id}>
                      <td>{index + 1}</td>
                      <td>
                        <div className="user-info">
                          <strong>{booking.user_name}</strong>
                          <small>{booking.user_email}</small>
                        </div>
                      </td>
                      <td>{booking.service_type}</td>
                      <td>{booking.consultation_type}</td>
                      <td>
                        {booking.date} at {booking.time}
                      </td>
                      <td>₹{booking.amount}</td>
                      <td>
                        <span className={`status-badge ${booking.status}`}>
                          {booking.status}
                        </span>
                      </td>
                      <td>
                        <select 
                          value={booking.status}
                          onChange={(e) => updateBookingStatus(booking.id, e.target.value)}
                          className="status-select"
                        >
                          <option value="pending">Pending</option>
                          <option value="confirmed">Confirmed</option>
                          <option value="completed">Completed</option>
                          <option value="cancelled">Cancelled</option>
                        </select>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;